import React, { MutableRefObject, useState } from 'react';
import styled from '@emotion/styled';
import get from 'lodash/get';
import { useAccessibilityContext, INCREASED_TEXT_LEVEL, DEFAULT_ZOOM_FACTOR } from '@vmos2/providers';

import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';
import { scaleCSSUnit } from '@vmos2/utils';

type Props = {
  accessibleContent?: string,
  align?: 'center' | 'left' | 'right' | 'spaceBetween',
  ariaHidden: boolean,
  ariaLive?: 'polite' | 'off',
  bold?: boolean,
  children: React.Node,
  className?: ?string,
  color?: String,
  dataTest?: String,
  display: 'inline' | 'block' | 'flex' | 'inline-block',
  groupContentForScreenReader: boolean,
  hideText?: boolean,
  id?: string,
  ignoreZoomScaling?: boolean,
  isHTML?: boolean,
  level?: 1 | 2 | 3 | 4 | 5,
  lineThrough?: boolean,
  margin?: 'none' | 'small' | 'normal' | 'large',
  minWidth: number,
  mode?: string,
  onClick: Function,
  strong?: boolean,
  tabIndex: -1 | 0,
  tag?: string,
  textRef?: MutableRefObject,
  type?: 'success' | 'danger' | 'warning' | 'info' | 'brand' | 'black' | 'grey' | 'lightGrey' | 'lighterGrey' | 'white',
  underline?: boolean,
  vAlign?: 'center' | 'flex-start',
  whiteSpace: string,
};

/**
 * ### Available modes
 * * `Bolder`: Make the font weight `700`.
 * * `Introduction`: Text on the 'Introduction' page
 * * `KioskHeader`: Text within the User/Loyalty information in the Kiosk header
 * * `LoyaltyAuthCardLoggedIn`: Text within the LoyaltyAuthCard component on the checkout page when logged in
 * * `LoyaltyAuthCardLoggedOut`: Text within the LoyaltyAuthCard component on the checkout page when logged out
 * * `NutritionalLawText`: Text from the Menu Overview page (The law text)
 * * `StoreSelection:UnavailableDelivery`: Text from the Store Selection page for unavailable delivery
 * * `RewardCounter`: Text within the RewardCounter component
 * * `Welcome:MemberCode`: Text in the 'Member Code' section on the Welcome page
 * * `Welcome:OrderNow`: Text in the 'Ready to eat?' section on the Welcome page
 * * `Welcome:Rewards`: Text in the 'My Rewards' section on the Welcome page
 * */

export const Text = withSpacingStyles(
  ({
    accessibleContent,
    align,
    ariaHidden = false,
    ariaLive,
    bold,
    children,
    className,
    color,
    dataTest,
    display = 'inline',
    groupContentForScreenReader = false,
    hideText = false,
    id,
    ignoreZoomScaling,
    isHTML,
    level = 3,
    lineThrough,
    margin,
    minWidth,
    mode,
    onClick,
    strong,
    tabIndex,
    tag,
    textRef,
    type,
    underline,
    vAlign,
    whiteSpace,
  }: Props) => {
    const componentTheme = useComponentTheme(theme => theme.components.Text, mode);
    const { zoomFactor } = useAccessibilityContext();
    const isZoomed = !!zoomFactor && zoomFactor !== DEFAULT_ZOOM_FACTOR;
    const [textContent, setTextContent] = useState();
    const addAccessibleText = accessibleContent || groupContentForScreenReader;

    const childElements = addAccessibleText ? (
      <>
        {/* `role="group"` stops screen readers from reading out the text twice in browse mode when there is a tabindex
        present — once when it focuses on the parent span, and then a second time when you move to the next element
        (which is the child of this span). */}
        <span className="hide-text-show-focus" ref={textRef} role="group" tabIndex={tabIndex}>
          {accessibleContent || textContent}
        </span>

        <AriaHiddenSpan
          aria-hidden="true"
          {...(!accessibleContent && {
            // Add a comma and space between each one so that the screen reader reads them out with pauses in between.
            // We can't use a full stop, because that makes things like 'Pepsi Max.' get announced out as 'Pepsi
            // Maximum'.
            ref: contentElement => setTextContent(contentElement?.innerText.replace(/\n+/giu, ', ')),
          })}
        >
          {children}
        </AriaHiddenSpan>
      </>
    ) : (
      children
    );

    return (
      <TextWrapper
        align={componentTheme.align || align}
        as={tag}
        bold={bold}
        className={hideText ? 'hide-text' : className}
        color={color}
        componentTheme={componentTheme}
        data-test={dataTest}
        display={display}
        id={id}
        ignoreZoomScaling={ignoreZoomScaling}
        isZoomed={isZoomed}
        level={level}
        lineThrough={lineThrough}
        marginType={margin}
        minWidth={minWidth}
        strong={strong}
        type={type}
        underline={underline}
        vAlign={vAlign}
        whiteSpace={componentTheme.whiteSpace || whiteSpace}
        {...(ariaHidden && { 'aria-hidden': true })}
        {...(ariaLive && { 'aria-live': ariaLive })}
        {...(addAccessibleText ? { css: { position: 'relative' } } : { ref: textRef, tabIndex })}
        {...(isHTML
          ? { dangerouslySetInnerHTML: { __html: children.replace(/<\/?p[^>]*>/g, '') } }
          : { children: childElements })}
        {...(onClick && { onClick })}
      />
    );
  }
);

Text.displayName = 'Text';

const alignStyles = {
  center: `display: block; text-align: center;`,
  right: `display: block; text-align: right;`,
  left: `display: block; text-align: left;`,
  spaceBetween: `display: flex; justify-content: space-between;`,
};

const marginStyles = {
  xs: 'margin: 0 0 8px',
  small: 'margin: 0 0 16px',
  normal: 'margin: 0 0 24px',
  large: 'margin: 0 0 32px',
  none: 'margin: 0',
};

const textColor = (theme, componentTheme, type, color) => {
  if (type) return theme.color[type];
  if (color) return color;
  return get(componentTheme, 'color', 'inherit');
};

const vAlignStyles = {
  center: `display: flex; align-items: center;`,
};

const AriaHiddenSpan = styled.span`
  display: contents;
`;

const TextWrapper = styled.span`
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}px;` : '')}
  display: ${({ display }) => display};
  font-size: ${({ componentTheme, level, ignoreZoomScaling, isZoomed }) =>
    ignoreZoomScaling && isZoomed
      ? scaleCSSUnit(componentTheme.fontSize[`level${level}`], INCREASED_TEXT_LEVEL)
      : componentTheme.fontSize[`level${level}`]};
  ${({ marginType }) =>
    marginType &&
    `
      display: block;
      ${marginStyles[marginType]};
    `};
  ${({ align }) => align && alignStyles[align]};
  ${({ vAlign }) => vAlign && vAlignStyles[vAlign]};
  ${({ lineThrough }) => lineThrough && 'text-decoration: line-through'};
  ${({ theme, componentTheme }) =>
    componentTheme.secondaryFontFamily && `font-family: ${theme.typography.secondaryFontFamily};`}
  font-weight: ${({ bold, strong, theme, componentTheme }) =>
    bold ? theme.fontWeight.bold : strong ? theme.fontWeight.medium : get(componentTheme, 'fontWeight', 'normal')};
  ${({ underline }) => underline && 'text-decoration: underline'};
  color: ${({ theme, componentTheme, type, color }) => textColor(theme, componentTheme, type, color)};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  ${({ componentTheme }) => componentTheme.backgroundColor && `background-color: ${componentTheme.backgroundColor}`};
  ${({ componentTheme }) => componentTheme.textTransform && `text-transform: ${componentTheme.textTransform}`};
  ${({ whiteSpace }) => whiteSpace && `white-space: ${whiteSpace}`};
`;
